import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';

import { Container } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper';

import { setLeasingData, setEicsData, setSpecificationsData, setRawSpecificationsData, setBuilderData, setModelsData, setErrorData } from 'store/entities/data';
import { setGrade, setConfig } from 'store/entities/config';
import { resetAppState } from 'store/entities/app';

import useLocale from 'hooks/useLocale';
import slugify from 'utils/slugify';

import LoadingSpinner from 'components/LoadingSpinner';
import NotFound from 'components/NotFound';
import { GradeCard } from 'components/Cards';
import { TechDataModal } from 'components/modals';

import { ReactComponent as ArrowLeft } from 'assets/images/icons/ArrowLeft.svg';
import { ReactComponent as ArrowRight } from 'assets/images/icons/ArrowRight.svg';

import { useLeasingDataRequest, useSpecificationsDataRequest, useCurrencyRateRequest, useEicsDataRequest, useModelsDataRequest } from 'api/requests';

const GradeSelection = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const locale = useLocale();
    const { modelSlug } = useParams();

    const { specification } = useSelector(state => state.config);
    const { leasingData, eicsData, specificationsData, rawSpecificationsData, errorData, modelsData } = useSelector(state => state.data);
    const { id: queryId, code: queryCode } = queryString.parse(location.search);

    const ENV = window.ENV[window.location.hostname] || window.ENV.default;

    const dataModels = useModelsDataRequest({
        params: {
            params: {
                brand: ENV.BRAND_NAME,
                isActive: true
            }
        },
        config: {
            enabled: !!modelSlug && !queryId && !queryCode && !modelsData.length,
        },
        success: (result) => {
            dispatch(setModelsData(result.data));
        },
        error: (error) => {
            dispatch(setErrorData({
                type: 'model',
                message: error.message,
                refetch: true
            }));
        }
    });

    const slugModel = modelSlug && dataModels.data ? dataModels.data.data.find(item => slugify(item.name) === modelSlug) : null;
    const slugNotFound = !!modelSlug && !queryId && !queryCode && dataModels.isFetched && !slugModel;

    const model = queryId || slugModel?.id;
    const code = queryCode || slugModel?.code;

    const [viewModal, setViewModal] = React.useState(null);
    const handleModalShow = (eic, type) => {
        setViewModal({ eic, type });
    };
    const handleModalHide = () => {
        setViewModal(null);
    };

    const gradesData = Object.keys(eicsData)
        .map(key => [...eicsData[key]]
            .sort((a, b) => a.price - b.price)[0] || null
        )
        .sort((a, b) => a.price - b.price);

    const selectGrade = (gradeId) => {
        const gradeData = gradesData.find(item => item.grade.id === gradeId) || {};
        const leasing = gradeData.leasing?.period?.[0];
        const gradeConfig = {
            model: gradeData.model?.id,
            specification: gradeData.eic,
            gradeId: gradeData.grade?.id,
            body: gradeData.body?.id,
            engine: gradeData.engine?.id,
            brand: ENV.BRAND_NAME,
            code: gradeData.model?.code,
            colorCode: gradeData.exterior?.code,
            interior: gradeData.interior?.code,
            wheels: gradeData.wheels?.code,
            leasingPeriod: leasing?.value,
            leasingMileage: leasing?.mileage[0].value
        };
        dispatch(resetAppState());
        dispatch(setGrade(gradeConfig));
        dispatch(setBuilderData(gradeData));
        //dispatch(setBodyData(eicsData[gradeConfig.gradeId]));
    };

    const dataLeasing = useLeasingDataRequest({
        params: {
            modelId: model
        },
        config: {
            enabled: false,
        },
        success: (result) => {
            dispatch(setLeasingData(result.data));
            dispatch(setEicsData(result.data.map(item => {
                const { specification, ...leasingData } = item;
                const thumb = rawSpecificationsData.find(item => item.grade.code === specification.grade.code)?.model.pictures.main;
                // : specificationsData[specification.grade.id]?.[0]?.model.pictures.main
                return {
                    ...specification,
                    grade: { ...specification.grade, thumb },
                    leasing: leasingData
                }
            })));
        },
        error: (error) => {
            dispatch(setErrorData({
                type: 'leasing',
                message: error.message,
                refetch: true
            }));
        }
    });

    //const dataEics = useEicsDataRequest({
    //    params: {
    //        ids: Object.keys(leasingData),
    //        params: {
    //            brand: ENV.BRAND_NAME,
    //            language: locale
    //        }
    //    },
    //    config: {
    //        enabled: !!Object.keys(leasingData).length,
    //    }
    //});
    //const isEicsSuccess = !!dataEics.length && dataEics.every(item => item.isSuccess);
    //const isEicsError = dataEics.some(item => item.isError);
    //const isEicsLoading = dataEics.some(item => item.isFetching);

    const dataSpecs = useSpecificationsDataRequest({
        params: {
            params: {
                'brand.name': ENV.BRAND_NAME,
                'model.id': model,
                'model.code': code,
                limit: 100,
                isActive: true
            }
        },
        config: {
            enabled: false,
        },
        success: (result) => {
            dispatch(setRawSpecificationsData(result.data));
            dispatch(setSpecificationsData(result.data));
            dataLeasing.refetch();
        },
        error: (error) => {
            dispatch(setErrorData({
                type: 'specifications',
                message: error.message,
                refetch: true
            }));
        }
    });

    //useCurrencyRateRequest({
    //    success: (result) => {
    //        const currencyConfig = {
    //            currencyRate: result.data.currencyRate,
    //            currencyCode: result.data.currencyCode
    //        };
    //        Object.keys(currencyConfig).forEach(key => {
    //            if (!currencyConfig[key]) delete currencyConfig[key];
    //        });
    //        if (Object.keys(currencyConfig).length) dispatch(setConfig(currencyConfig));
    //    },
    //    error: (error) => {
    //        console.log('Currency rate error: ', error.message);
    //    }
    //});

    React.useEffect(() => {
        if (slugNotFound) navigate('/', { replace: true });
    }, [slugNotFound]);

    React.useEffect(() => {
        if (model || code) {
            dataSpecs.refetch();
        } else if (!modelSlug) {
            dispatch(setErrorData({
                type: 'model',
                message: t('error_missing_id', 'Model ID or model CODE is missing'),
                redirect: t('fallback_url')
            }));
        }
    }, [model, code, modelSlug]);


    //React.useEffect(() => {
    //    if (isEicsSuccess) {
    //        dispatch(setEicsData(dataEics.map(item => ({
    //            ...item.data.data,
    //            grade: { ...item.data.data.grade, thumb: specificationsData[item.data.data.grade.id]?.[0]?.model.pictures.main },
    //            leasing: leasingData[item.data.data.eic]
    //        }))));
    //    }
    //}, [isEicsLoading, isEicsError, isEicsSuccess]);


    React.useEffect(() => {
        const firstGradeId = gradesData[0]?.grade?.id;
        if (!specification && firstGradeId) selectGrade(firstGradeId);
    }, [eicsData]);

    const handleGradeSelect = (gradeId) => {
        if (gradeId) {
            selectGrade(gradeId);
            navigate(`/${i18n.language}/select`);
        } else {
            console.log('Missing grade ID');
        }
    };

    const swiperProps = {
        className: 'card-swiper',
        cssMode: true,
        slidesPerView: 'auto',
        navigation: {
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
        },
        pagination: {
            clickable: true,
        },
        mousewheel: true,
        keyboard: true,
        modules: [Navigation, Pagination, Mousewheel, Keyboard]
    };

    const noData = dataLeasing.isFetchedAfterMount && !Object.keys(leasingData).length;

    return (<>
        {(dataModels.isFetching || dataLeasing.isLoading || !dataLeasing.isFetchedAfterMount) && (
            <LoadingSpinner />
        )}
        {!errorData?.type ? (
            <section className="c-section c-grades">
                <Container>
                    <h5 className="c-section__subheader">{t('grade_selection_descr')}</h5>
                    <Swiper {...swiperProps}>
                        {gradesData.map(grade => (
                            <SwiperSlide key={grade.eic}>
                                <GradeCard gradeData={grade} onSelect={handleGradeSelect} onDetails={handleModalShow} />
                            </SwiperSlide>
                        ))}
                        <button className="swiper-button swiper-button-prev" type="button">
                            <ArrowLeft />
                        </button>
                        <button className="swiper-button swiper-button-next" type="button">
                            <ArrowRight />
                        </button>
                    </Swiper>
                    {noData && (
                        <h5 className="c-section__subheader">{t('error_cars_not_found', 'No cars found')}</h5>
                    )}
                </Container>
                <TechDataModal show={!!viewModal} onClose={handleModalHide} data={viewModal} />
            </section>
        ) : (
            <NotFound error={errorData} onRefetch={dataSpecs.refetch} />
        )}
    </>);
};

export default GradeSelection;
